import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import {
  CommonAreaType,
  CreateSpacePayload,
  ParkingSpotType,
  RoomType,
  SpaceDiscriminator,
  SpaceReadModel,
  useGetSpacesByIdQuery,
  usePostCommonAreasMutation,
  usePostParkingSpotsMutation,
  usePostRoomsMutation,
  usePostSpacesMutation,
} from '@client/api';
import { Skeleton, useRouteDocumentTitle } from '@client/toolkit';

import { PortfolioPaths } from '../../../portfolio.router';
import { SpaceForm, SpaceFormData } from '../../components/space.form';

export const SpaceCreateRoute = () => {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const navigate = useNavigate();
  useRouteDocumentTitle(
    t('spaceCreate.documentTitle', { entity: tGlobal('entity.space') }),
    PortfolioPaths.BuildingChildCreate,
  );

  const [mutation] = usePostSpacesMutation();
  const [commonAreaMutation] = usePostCommonAreasMutation();
  const [roomMutation] = usePostRoomsMutation();
  const [parkingSpotMutation] = usePostParkingSpotsMutation();

  const { buildingId, spaceId, createDiscriminator } = useParams<{
    buildingId: string;
    spaceId: string;
    discriminator: string;
    createDiscriminator: SpaceDiscriminator;
  }>();

  const handleClose = () => {
    navigate(-1);
  };

  const handleSubmit = async (data: SpaceFormData) => {
    if (!spaceId && !buildingId) return;

    let createdSpace: SpaceReadModel;

    const payload: CreateSpacePayload = {
      discriminator: createDiscriminator ?? 'Generic',
      parentId: data.parent?.id || spaceId || buildingId, // will mostly never happen because parent.id is required by validation
      name: data.name,
      description: data.description,
      areaMetric: data.areaMetric,
      rentPriceId: data.rentPriceId,
      assetClass: data.assetClass || undefined,
    };

    try {
      switch (createDiscriminator) {
        case 'CommonArea':
          createdSpace = await commonAreaMutation({
            body: {
              ...payload,
              operationalCostPriceId: data.operationalCostPriceId,
              type: data.commonAreaType as CommonAreaType,
              parentId: data.parent?.id as string,
            },
          }).unwrap();
          break;
        case 'Room':
          createdSpace = await roomMutation({
            body: {
              ...payload,
              type: data.roomType as RoomType,
              parentId: data.parent?.id as string,
            },
          }).unwrap();
          break;
        case 'ParkingSpot':
          createdSpace = await parkingSpotMutation({
            body: {
              ...payload,
              type: data.parkingSpotType as ParkingSpotType,
              parentId: data.parent?.id as string,
            },
          }).unwrap();
          break;
        default:
          createdSpace = await mutation({
            body: payload,
          }).unwrap();
      }
    } catch (e) {
      return;
    }

    if (createdSpace?.id != null) {
      navigate(
        generatePath(PortfolioPaths.Space, {
          buildingId,
          spaceId: createdSpace.id,
          discriminator: createdSpace.discriminator,
        }),
        { replace: true },
      );
    }
  };

  const { currentData: defaultParent, isFetching: isFetchingParent } = useGetSpacesByIdQuery(
    { id: (spaceId || buildingId) ?? '' },
    { skip: !spaceId && !buildingId },
  );

  return (
    <SpaceForm
      title={
        ready ? (
          t('spaceCreate.title', {
            entity: createDiscriminator
              ? tType(`SpaceDiscriminator.${createDiscriminator}`).toLowerCase()
              : tGlobal('entity.space'),
          })
        ) : (
          <Skeleton variant="text" />
        )
      }
      onSubmit={handleSubmit}
      onClose={handleClose}
      isFetching={isFetchingParent}
      defaultValues={{
        discriminator: createDiscriminator,
        parent: defaultParent,
      }}
      create
    />
  );
};
